import { useEffect, useState, useRef, useMemo } from "react";
import api, { API } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { SurfaceCard } from "@/components/common/Cards";
import { StatusBadge } from "@/components/common/StatusBadge";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { FolderOpen, FileText, FileImage, Upload, Download, Trash2, Eye, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const ALLOWED = ["pdf", "jpg", "jpeg", "png", "webp", "heic", "doc", "docx", "txt", "csv"];
const MAX_BYTES = 25 * 1024 * 1024;
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx,.txt,.csv";

const fmtSize = (s) => {
  if (typeof s !== "number") return s || "—";
  if (s < 1024) return `${s} B`;
  if (s < 1024 * 1024) return `${(s / 1024).toFixed(0)} KB`;
  return `${(s / 1024 / 1024).toFixed(1)} MB`;
};
const isImage = (d) => (d.content_type || "").startsWith("image/") || ["jpg", "jpeg", "png", "webp", "heic"].includes(d.ext);

export default function Documents() {
  const [items, setItems] = useState(null);
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState("none");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const fileRef = useRef(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("contaha_token") : "";

  const load = () => api.get("/documents").then((r) => setItems(r.data)).catch(() => { setItems([]); setError("Couldn't load documents."); });
  useEffect(() => { load(); api.get("/patients").then((r) => setPatients(r.data)).catch(() => {}); }, []);

  const fileUrl = (d) => `${API}/files/${d.storage_path}?auth=${token}`;

  const onPick = () => fileRef.current?.click();
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    setError(null);
    for (const f of files) {
      const ext = f.name.split(".").pop().toLowerCase();
      if (!ALLOWED.includes(ext)) { toast.error(`"${f.name}": unsupported type. Allowed: PDF, JPG, PNG, WEBP, DOC, DOCX, TXT, CSV.`); continue; }
      if (f.size > MAX_BYTES) { toast.error(`"${f.name}" is too large (max 25 MB).`); continue; }
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", f);
        if (patientId && patientId !== "none") fd.append("patient_id", patientId);
        fd.append("doc_type", ext === "pdf" ? "PDF" : isImage({ ext }) ? "Image" : "Document");
        await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`Uploaded ${f.name}`);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Upload failed for ${f.name}`);
      } finally { setUploading(false); }
    }
    load();
  };

  const download = async (d) => {
    setDownloading(d.id);
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: d.content_type || "application/octet-stream" }));
      const a = document.createElement("a"); a.href = url; a.download = d.name; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
    finally { setDownloading(null); }
  };

  const doDelete = async () => {
    const d = confirmDel; setConfirmDel(null);
    try { await api.delete(`/documents/${d.id}`); toast.success("Document deleted"); load(); }
    catch { toast.error("Could not delete the document"); }
  };

  const patientName = useMemo(() => patients.find((p) => p.id === patientId)?.name, [patients, patientId]);

  return (
    <div>
      <PageHeader title="Documents" subtitle="Upload and manage clinical files — PDFs, imaging, consents and reports" breadcrumb={["Clinical", "Documents"]}
        actions={
          <div className="flex items-center gap-2">
            <Select value={patientId} onValueChange={setPatientId}>
              <SelectTrigger className="h-9 w-[180px] rounded-lg" data-testid="doc-patient-select"><SelectValue placeholder="Link to patient (optional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No patient link</SelectItem>
                {patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <input ref={fileRef} type="file" accept={ACCEPT} multiple className="hidden" onChange={onFiles} data-testid="doc-file-input" />
            <Button onClick={onPick} disabled={uploading} data-testid="doc-upload-btn" className="bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg">
              {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}{uploading ? "Uploading…" : "Upload"}
            </Button>
          </div>
        } />

      {patientId !== "none" && <p className="mb-3 text-xs text-[#8B9698]">New uploads will be linked to <b className="text-[#101516] dark:text-white">{patientName}</b>.</p>}

      {/* Dropzone */}
      <div
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); onFiles({ target: { files: e.dataTransfer.files, value: "" } }); }}
        onClick={onPick}
        data-testid="doc-dropzone"
        className="mb-5 flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-[#E2E8F0] dark:border-[#223139] bg-[#F4FBF7]/50 dark:bg-[#0E1A21]/50 py-8 text-center transition-colors hover:border-[#00A85A]/50">
        <Upload className="h-7 w-7 text-[#00A85A]" />
        <div className="mt-2 text-sm font-semibold text-[#101516] dark:text-white">Drag & drop files here, or click to browse</div>
        <div className="mt-1 text-xs text-[#8B9698]">PDF, JPG, PNG, WEBP, DOC, DOCX, TXT, CSV · up to 25 MB</div>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-500/30 dark:bg-red-500/10">
          <AlertTriangle className="h-4 w-4" />{error}
          <button onClick={() => { setError(null); setItems(null); load(); }} className="ml-auto font-semibold underline">Retry</button>
        </div>
      )}

      {items === null ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-[68px] rounded-2xl" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState icon={FolderOpen} title="No documents yet" description="Upload a PDF, image or clinical file to get started." testId="documents-empty" />
      ) : (
        <div className="space-y-3" data-testid="documents-list">
          {items.map((d) => (
            <SurfaceCard key={d.id} className="p-4" data-testid={`doc-item-${d.id}`}>
              <div className="flex items-center gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#E8F7F0] dark:bg-[#00A85A]/15">
                  {isImage(d) ? <FileImage className="h-5 w-5 text-[#00A85A]" /> : <FileText className="h-5 w-5 text-[#00A85A]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-[#101516] dark:text-white truncate">{d.name}</div>
                  <div className="text-xs text-[#8B9698] truncate">{[d.patient_name, fmtSize(d.size), formatDate(d.created_at)].filter(Boolean).join(" · ")}</div>
                </div>
                <StatusBadge status="info" label={(d.ext || d.type || "file").toUpperCase()} />
                <div className="flex items-center gap-1">
                  <a href={fileUrl(d)} target="_blank" rel="noreferrer" data-testid={`doc-view-${d.id}`} title="Open" className="flex h-8 w-8 items-center justify-center rounded-lg text-[#8B9698] hover:bg-black/5 hover:text-[#00A85A] dark:hover:bg-white/10"><Eye className="h-4 w-4" /></a>
                  <button onClick={() => download(d)} data-testid={`doc-download-${d.id}`} title="Download" className="flex h-8 w-8 items-center justify-center rounded-lg text-[#8B9698] hover:bg-black/5 hover:text-[#00A85A] dark:hover:bg-white/10">{downloading === d.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}</button>
                  <button onClick={() => setConfirmDel(d)} data-testid={`doc-delete-${d.id}`} title="Delete" className="flex h-8 w-8 items-center justify-center rounded-lg text-[#8B9698] hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </SurfaceCard>
          ))}
        </div>
      )}

      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="doc-delete-dialog">
          <DialogHeader><DialogTitle>Delete document?</DialogTitle><DialogDescription>This permanently removes the document from this doctor's records.</DialogDescription></DialogHeader>
          <p className="text-sm text-[#687173] dark:text-[#8B9698]">"{confirmDel?.name}" will be removed. This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)} className="rounded-lg">Cancel</Button>
            <Button onClick={doDelete} data-testid="doc-delete-confirm" className="rounded-lg bg-red-500 hover:bg-red-600 text-white">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
